import { Box, Dialog, DialogContent, IconButton, Stack, Typography } from "@mui/material";
import { Alert } from "Components/_Shared/Alert";
import { Button } from "Components/_Shared/Buttons/Button";
import { Flex } from "Components/_Shared/Core/Flex";
import { IconBadge } from "Components/_Shared/IconBadge";
import { ReactComponent as CheckIcon } from "Components/_Shared/Icons/Assets/check.svg";
import { ReactComponent as CloseIcon } from "Components/_Shared/Icons/Assets/close.svg";
import { useResendQuestionnaireSignRequest, useVerifyQuestionnaireSign } from "Infrastructure/ApiZfp/AnalyzerApi";
import { VerifySignForbiddenErrorCode } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";

const CODE_LENGTH = 6;

type Props = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void;
  readonly verificationToken: string;
};

export const QuestionnaireEvaluationVerifyModal = ({ isOpen, onClose, onConfirm, verificationToken }: Props) => {
  const { t } = useResource();
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(""));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentVerificationToken, setCurrentVerificationToken] = useState(verificationToken);
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(CODE_LENGTH).fill(null));

  const { mutateAsync: resendCode, isPending: isResending } = useResendQuestionnaireSignRequest();
  const { mutateAsync: verifyCode, isPending: isVerifying } = useVerifyQuestionnaireSign();

  useEffect(() => {
    if (!isOpen) {
      setDigits(Array(CODE_LENGTH).fill(""));
      setErrorMessage(null);
      setCurrentVerificationToken(verificationToken);
    } else {
      setCurrentVerificationToken(verificationToken);
      setTimeout(() => inputRefs.current[0]?.focus(), 0);
    }
  }, [isOpen, verificationToken]);

  const handleClose = () => {
    onClose();
  };

  const handleChange = (index: number, value: string) => {
    const filtered = value.replace(/\D/g, "");
    const char = filtered.slice(-1) || "";
    const newDigits = [...digits];
    newDigits[index] = char;
    setDigits(newDigits);

    if (char && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === "Enter" && isComplete && !isVerifying) {
      handleConfirm();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    const newDigits = [...digits];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setDigits(newDigits);
    const focusIndex = Math.min(pasted.length, CODE_LENGTH - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleConfirm = async () => {
    setErrorMessage(null);
    try {
      await verifyCode({
        analysisId,
        versionId,
        data: { verificationToken: currentVerificationToken, smsCode: digits.join("") },
      });
      toast.success(t(Resources.Clients.Detail.SMSVerifyModal.VerificationSuccess));
      handleClose();
      onConfirm();
    } catch (error) {
      if ((error as any)?.code === VerifySignForbiddenErrorCode.verification_invalid) {
        setErrorMessage(t(Resources.Clients.Detail.SMSVerifyModal.InvalidCode));
      } else {
        setErrorMessage((error as any)?.message || (error as any)?.data?.error?.message || t(Resources.Common.Error));
      }
    }
  };

  const handleResend = async () => {
    try {
      const response = await resendCode({ analysisId, versionId, data: { verificationToken: currentVerificationToken } });
      setCurrentVerificationToken(response.verificationToken);
      toast.success(t(Resources.Clients.Detail.SMSVerifyModal.CodeSent));
    } catch {
      toast.error(t(Resources.AnalysisDetail.Toast.ActionError));
    }
  };

  const isComplete = digits.every((d) => d !== "");

  return (
    <Dialog
      open={isOpen}
      onClose={(_, reason) => (reason === "backdropClick" ? undefined : handleClose())}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { borderRadius: Radius.XXXL } } }}
    >
      <DialogContent>
        <Stack spacing={Spacing.XXL} alignItems="center">
          <Flex justifyContent="flex-end" alignItems="center" sx={{ width: "100%" }}>
            <IconButton onClick={handleClose} aria-label={t(Resources.Common.Close)}>
              <CloseIcon />
            </IconButton>
          </Flex>
          <Stack px={Spacing.XXXL} alignItems="center" spacing={Spacing.XXXL}>
            <IconBadge color="success" icon={<CheckIcon width={35} height={35} />} />

            <Box textAlign="center">
              <Typography variant="h2" gutterBottom>
                {t(Resources.Clients.Detail.SMSVerifyModal.CodeSent)}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {t(Resources.Clients.Detail.SMSVerifyModal.Subtitle)}
              </Typography>
            </Box>

            <Flex gap={Spacing.M}>
              {(["d0", "d1", "d2", "d3", "d4", "d5"] as const).map((key, index) => (
                <Box
                  key={key}
                  ref={(el) => {
                    inputRefs.current[index] = el as HTMLInputElement | null;
                  }}
                  component="input"
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digits[index]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(index, e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, e)}
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => handlePaste(e)}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.select()}
                  aria-label={`Verification code digit ${index + 1}`}
                  autoComplete="off"
                  data-1p-ignore
                  data-bwignore
                  data-lpignore
                  sx={{
                    width: 52,
                    height: 60,
                    textAlign: "center",
                    fontSize: 24,
                    fontWeight: 600,
                    color: "grey.600",
                    border: "2px solid",
                    borderColor: "grey.200",
                    borderRadius: Radius.M,
                    outline: "none",
                    caretColor: "transparent",
                    background: "white",
                    "&:focus": {
                      borderColor: "primary.main",
                    },
                  }}
                />
              ))}
            </Flex>

            {errorMessage && <Alert variant="error" text={errorMessage} />}

            <Stack spacing={Spacing.M} width="100%" alignItems="center">
              <Button variant="text" onClick={handleResend} disabled={isResending}>
                {t(Resources.Clients.Detail.SMSVerifyModal.ResendCode)}
              </Button>
              <Button fullWidth disabled={isVerifying || !isComplete} onClick={handleConfirm}>
                {t(Resources.Common.Confirm)}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
